import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { 
  Video, Eye, Users, Clock, TrendingUp, BarChart3, 
  Play, MessageCircle, ThumbsUp, LogOut, Settings,
  ChevronRight, Crown, Loader2, AlertCircle, Film,
  Lock, Mail, Check, X, Share2, Copy, ExternalLink,
  Globe, PieChart, Target, DollarSign, Calendar,
  ThumbsDown, Sparkles, Activity, ArrowUpRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Chart colors matching the Traffic Sources theme
const CHART_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'];

const CreatorPortalDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [creator, setCreator] = useState(null);
  const [overview, setOverview] = useState(null);
  const [videos, setVideos] = useState([]);
  const [reels, setReels] = useState([]);
  const [subscribers, setSubscribers] = useState(null);
  const [adRevenue, setAdRevenue] = useState(null);
  const [revenueSummary, setRevenueSummary] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoComments, setVideoComments] = useState([]);
  
  // Share modal state
  const [shareVideo, setShareVideo] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  
  // Account settings state
  const [showSettings, setShowSettings] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('creatorPortalToken');
    return { Authorization: `Bearer ${token}` };
  };

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('creatorPortalToken');
    const user = localStorage.getItem('creatorPortalUser');
    
    if (!token) {
      navigate('/creator/login');
      return;
    }
    
    if (user) {
      setCreator(JSON.parse(user));
    }
    
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      const headers = getAuthHeaders();
      
      const [overviewRes, videosRes, reelsRes, subscribersRes, revenueSummaryRes] = await Promise.all([
        axios.get(`${API}/api/creator-portal/dashboard/overview`, { headers }),
        axios.get(`${API}/api/creator-portal/dashboard/videos`, { headers }),
        axios.get(`${API}/api/creator-portal/dashboard/reels`, { headers }),
        axios.get(`${API}/api/creator-portal/dashboard/subscribers`, { headers }),
        axios.get(`${API}/api/creator-portal/dashboard/revenue-summary`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setOverview(overviewRes.data);
      setVideos(videosRes.data.videos || []);
      setReels(reelsRes.data.reels || []);
      setSubscribers(subscribersRes.data);
      setRevenueSummary(revenueSummaryRes.data);
      
      // Update creator info with ad revenue visibility
      if (overviewRes.data.creator_name) {
        setCreator(prev => ({
          ...prev,
          creator_name: overviewRes.data.creator_name,
          profile_image_url: overviewRes.data.profile_image_url,
          show_ad_revenue: overviewRes.data.show_ad_revenue || false
        }));
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('creatorPortalToken');
        localStorage.removeItem('creatorPortalUser');
        navigate('/creator/login');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch ad revenue data when ad revenue tab is selected
  const fetchAdRevenue = async (period = 'all') => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/api/creator-portal/dashboard/ad-revenue?period=${period}`, { headers });
      setAdRevenue(res.data);
    } catch (err) {
      console.error('Error fetching ad revenue:', err);
      if (err.response?.status === 403) {
        // Ad revenue not enabled for this account
        setAdRevenue({ disabled: true });
      }
    }
  };
  
  // Load ad revenue data when tab is selected
  useEffect(() => {
    if (activeTab === 'ad-revenue' && !adRevenue) {
      fetchAdRevenue();
    }
  }, [activeTab]);

  const fetchVideoComments = async (videoId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/api/creator-portal/dashboard/video/${videoId}/comments`, { headers });
      setVideoComments(res.data.comments || []);
      setSelectedVideo(videos.find(v => v.id === videoId));
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('creatorPortalToken');
    localStorage.removeItem('creatorPortalUser');
    navigate('/creator/login');
  };

  const handleUpdateEmail = async () => {
    setSettingsError('');
    setSettingsSuccess('');
    
    if (!newEmail || !currentPassword) {
      setSettingsError('Please fill in all fields');
      return;
    }
    
    setSavingSettings(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/api/creator-portal/auth/update-email`, {
        new_email: newEmail,
        password: currentPassword
      }, { headers });
      
      setSettingsSuccess('Email updated successfully');
      setNewEmail('');
      setCurrentPassword('');
      
      // Update local storage
      const user = JSON.parse(localStorage.getItem('creatorPortalUser') || '{}');
      user.email = newEmail;
      localStorage.setItem('creatorPortalUser', JSON.stringify(user));
      setCreator(prev => ({ ...prev, email: newEmail }));
    } catch (err) {
      setSettingsError(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleChangePassword = async () => {
    setSettingsError('');
    setSettingsSuccess('');
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      setSettingsError('Please fill in all fields');
      return;
    }
    
    if (newPassword.length < 8) {
      setSettingsError('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setSettingsError('New passwords do not match');
      return;
    }
    
    setSavingSettings(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/api/creator-portal/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers });
      
      setSettingsSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setSettingsError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingSettings(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Generate share URL for a video
  const getVideoShareUrl = (video) => {
    const baseUrl = window.location.origin;
    if (video.slug) {
      return `${baseUrl}/vod/v/${video.slug}`;
    }
    return `${baseUrl}/vod/watch/${video.id}`;
  };

  // Copy share URL to clipboard
  const copyShareUrl = (video) => {
    const url = getVideoShareUrl(video);
    navigator.clipboard.writeText(url);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  // Open share modal
  const openShareModal = (video) => {
    setShareVideo(video);
    setShareCopied(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 text-purple-500 animate-spin" />
          <p className="text-gray-400">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header - Matching Traffic Source Style */}
      <header className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* LTV VOD Logo */}
              <a href="/vod" className="flex-shrink-0">
                <img 
                  src="/ltv-vod-logo.png" 
                  alt="LTV VOD" 
                  className="h-10"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </a>
              
              {/* Divider */}
              <div className="hidden sm:block h-8 w-px bg-gray-700" />
              
              {/* Creator Info */}
              <div className="flex items-center gap-4">
                {creator?.profile_image_url ? (
                  <img 
                    src={creator.profile_image_url} 
                    alt="" 
                    className="w-10 h-10 rounded-xl object-cover border-2 border-purple-500/50"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                    <Video className="h-5 w-5" />
                  </div>
                )}
                <div className="hidden sm:block">
                  <h1 className="font-bold text-lg">{creator?.creator_name || 'Creator Portal'}</h1>
                  <p className="text-xs text-gray-400">Channel Analytics</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="border-gray-700 hover:bg-gray-800"
              >
                <Settings className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Account</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-gray-700 hover:bg-red-900/50 hover:border-red-500/50 hover:text-red-400"
              >
                <LogOut className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation - Traffic Source Style */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-1 overflow-x-auto py-2">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3, color: 'green' },
              { id: 'videos', label: 'Videos', icon: Video, color: 'blue' },
              { id: 'reels', label: 'Reels', icon: Film, color: 'pink' },
              { id: 'subscribers', label: 'Subscribers', icon: Users, color: 'purple' },
              ...(creator?.show_ad_revenue ? [{ id: 'ad-revenue', label: 'Ad Revenue', icon: DollarSign, color: 'yellow' }] : [])
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? `bg-${tab.color}-600/30 text-${tab.color}-400 border border-${tab.color}-500/50`
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
                style={activeTab === tab.id ? {
                  background: `linear-gradient(135deg, ${
                    tab.color === 'green' ? 'rgba(16, 185, 129, 0.2)' : 
                    tab.color === 'blue' ? 'rgba(59, 130, 246, 0.2)' : 
                    tab.color === 'pink' ? 'rgba(236, 72, 153, 0.2)' : 
                    tab.color === 'yellow' ? 'rgba(234, 179, 8, 0.2)' :
                    'rgba(139, 92, 246, 0.2)'
                  }, transparent)`,
                  borderColor: 
                    tab.color === 'green' ? 'rgba(16, 185, 129, 0.5)' : 
                    tab.color === 'blue' ? 'rgba(59, 130, 246, 0.5)' : 
                    tab.color === 'pink' ? 'rgba(236, 72, 153, 0.5)' : 
                    tab.color === 'yellow' ? 'rgba(234, 179, 8, 0.5)' :
                    'rgba(139, 92, 246, 0.5)'
                } : {}}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* ==================== OVERVIEW TAB ==================== */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards - Traffic Source Style Gradient Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-4 border border-purple-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Video className="h-5 w-5 text-purple-400" />
                  <span className="text-gray-300 text-xs">Total Videos</span>
                </div>
                <p className="text-3xl font-bold text-white">{overview?.overview?.total_videos || 0}</p>
                <p className="text-xs text-purple-300 mt-1">Published content</p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-4 border border-blue-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="h-5 w-5 text-blue-400" />
                  <span className="text-gray-300 text-xs">Total Views</span>
                </div>
                <p className="text-3xl font-bold text-white">{formatNumber(overview?.overview?.total_views)}</p>
                <p className="text-xs text-blue-300 mt-1">All-time views</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/20 rounded-xl p-4 border border-green-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-5 w-5 text-green-400" />
                  <span className="text-gray-300 text-xs">Watch Time</span>
                </div>
                <p className="text-3xl font-bold text-white">{overview?.overview?.total_watch_hours || 0}h</p>
                <p className="text-xs text-green-300 mt-1">{overview?.overview?.total_watch_minutes || 0} minutes</p>
              </div>
              
              <div className="bg-gradient-to-br from-pink-600/30 to-rose-600/20 rounded-xl p-4 border border-pink-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-pink-400" />
                  <span className="text-gray-300 text-xs">Subscribers</span>
                </div>
                <p className="text-3xl font-bold text-white">{formatNumber(overview?.overview?.subscriber_count)}</p>
                <p className="text-xs text-pink-300 mt-1">Channel subscribers</p>
              </div>
              
              {overview?.revenue && (
                <div className="bg-gradient-to-br from-yellow-600/30 to-orange-600/20 rounded-xl p-4 border border-yellow-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-yellow-400" />
                    <span className="text-gray-300 text-xs">Purchase Revenue</span>
                  </div>
                  <p className="text-3xl font-bold text-white">£{overview.revenue.total_revenue?.toFixed(2)}</p>
                  <p className="text-xs text-yellow-300 mt-1">{overview.revenue.purchase_count} purchases</p>
                </div>
              )}
              
              {revenueSummary?.show_ad_revenue && (
                <div className="bg-gradient-to-br from-emerald-600/30 to-teal-600/20 rounded-xl p-4 border border-emerald-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="h-5 w-5 text-emerald-400" />
                    <span className="text-gray-300 text-xs">Ad Revenue</span>
                  </div>
                  <p className="text-3xl font-bold text-white">£{revenueSummary.ad_revenue?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-emerald-300 mt-1">{formatNumber(revenueSummary.ad_impressions)} impressions</p>
                </div>
              )}
            </div>

            {/* Main Content Grid - Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Performing Video */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                <div className="p-4 border-b border-gray-800">
                  <h3 className="font-bold flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-yellow-400" />
                    Top Performing Video
                  </h3>
                </div>
                <div className="p-6">
                  {overview?.top_video ? (
                    <div className="flex gap-4">
                      <div className="w-40 aspect-video rounded-lg overflow-hidden bg-gray-800 flex-shrink-0 relative">
                        {overview.top_video.thumbnail_url ? (
                          <img 
                            src={overview.top_video.thumbnail_url} 
                            alt="" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Play className="h-10 w-10 text-gray-600" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-2 left-2 flex items-center gap-1 text-white text-xs">
                          <Eye className="h-3 w-3" />
                          {formatNumber(overview.top_video.view_count)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-white line-clamp-2">{overview.top_video.title}</h4>
                        <div className="flex items-center gap-3 mt-2 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(overview.top_video.created_at)}
                          </span>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                            <Sparkles className="h-3 w-3 mr-1" />
                            Best Performer
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Video className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No videos yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Performance Distribution */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                <div className="p-4 border-b border-gray-800">
                  <h3 className="font-bold flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-green-400" />
                    Content Performance
                  </h3>
                </div>
                <div className="p-6">
                  {videos.length > 0 ? (
                    <div className="space-y-4">
                      {videos.slice(0, 5).map((video, idx) => {
                        const maxViews = Math.max(...videos.map(v => v.analytics?.views || 0));
                        const pct = maxViews > 0 ? ((video.analytics?.views || 0) / maxViews) * 100 : 0;
                        return (
                          <div key={video.id}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-gray-300 truncate max-w-[200px]">{video.title}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-white">{formatNumber(video.analytics?.views)}</span>
                                <span className="text-xs text-gray-400">views</span>
                              </div>
                            </div>
                            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                              <div 
                                className="h-full rounded-full transition-all" 
                                style={{ 
                                  width: `${Math.max(pct, 5)}%`, 
                                  backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] 
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No performance data yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Engagement Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Total Engagement */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                <div className="p-4 border-b border-gray-800">
                  <h3 className="font-bold flex items-center gap-2">
                    <ThumbsUp className="h-5 w-5 text-blue-400" />
                    Engagement
                  </h3>
                </div>
                <div className="p-4">
                  {videos.length > 0 ? (
                    <div className="space-y-4">
                      {[
                        { label: 'Total Likes', value: videos.reduce((a, v) => a + (v.analytics?.likes || 0), 0), icon: ThumbsUp, color: '#3b82f6' },
                        { label: 'Total Comments', value: videos.reduce((a, v) => a + (v.analytics?.comments || 0), 0), icon: MessageCircle, color: '#10b981' },
                        { label: 'Unique Viewers', value: videos.reduce((a, v) => a + (v.analytics?.unique_viewers || 0), 0), icon: Users, color: '#8b5cf6' }
                      ].map((stat, idx) => (
                        <div key={stat.label} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <stat.icon className="h-4 w-4" style={{ color: stat.color }} />
                            <span className="text-sm text-gray-300">{stat.label}</span>
                          </div>
                          <span className="text-lg font-bold text-white">{formatNumber(stat.value)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">No engagement data</div>
                  )}
                </div>
              </div>

              {/* Recent Subscribers */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                <div className="p-4 border-b border-gray-800">
                  <h3 className="font-bold flex items-center gap-2">
                    <Users className="h-5 w-5 text-pink-400" />
                    Recent Growth
                  </h3>
                </div>
                <div className="p-4">
                  {subscribers ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-300">New (30 days)</span>
                        <span className="text-lg font-bold text-green-400">+{subscribers.recent_subscribers_30d}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-300">Total Subscribers</span>
                        <span className="text-lg font-bold text-white">{formatNumber(subscribers.total_subscribers)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-300">Avg Monthly</span>
                        <span className="text-lg font-bold text-blue-400">
                          {subscribers.growth?.length > 0 
                            ? Math.round(subscribers.growth.reduce((a, b) => a + b.new_subscribers, 0) / subscribers.growth.length)
                            : 0}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">No subscriber data</div>
                  )}
                </div>
              </div>

              {/* Avg Engagement Rate */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                <div className="p-4 border-b border-gray-800">
                  <h3 className="font-bold flex items-center gap-2">
                    <Activity className="h-5 w-5 text-green-400" />
                    Engagement Rate
                  </h3>
                </div>
                <div className="p-4">
                  {videos.length > 0 ? (
                    <div className="text-center py-4">
                      <div className="text-5xl font-bold text-white mb-2">
                        {(videos.reduce((a, v) => a + (v.analytics?.engagement_rate || 0), 0) / videos.length).toFixed(1)}%
                      </div>
                      <p className="text-sm text-gray-400">Average across all videos</p>
                      <div className="mt-4 flex justify-center gap-2">
                        {(() => {
                          const avgRate = videos.reduce((a, v) => a + (v.analytics?.engagement_rate || 0), 0) / videos.length;
                          if (avgRate > 5) return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Excellent</Badge>;
                          if (avgRate > 2) return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Good</Badge>;
                          return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Building</Badge>;
                        })()}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">No engagement data</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== VIDEOS TAB ==================== */}
        {activeTab === 'videos' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Video Analytics</h2>
                <p className="text-sm text-gray-400 mt-1">Detailed performance metrics for your content</p>
              </div>
              <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30">
                {videos.length} videos
              </Badge>
            </div>
            
            {videos.length === 0 ? (
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-12 text-center">
                <Video className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400 text-lg">No videos found</p>
                <p className="text-sm text-gray-500 mt-1">Videos assigned to your profile will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {videos.map((video, idx) => (
                  <div key={video.id} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden hover:border-purple-500/50 transition-colors">
                    <div className="p-5">
                      <div className="flex gap-5">
                        {/* Thumbnail */}
                        <div className="w-44 aspect-video rounded-lg overflow-hidden bg-gray-800 flex-shrink-0 relative group">
                          {video.thumbnail_url ? (
                            <img 
                              src={video.thumbnail_url} 
                              alt="" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className="h-10 w-10 text-gray-600" />
                            </div>
                          )}
                          {video.duration > 0 && (
                            <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                              {formatDuration(video.duration)}
                            </span>
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h3 className="font-semibold text-white text-lg line-clamp-1">{video.title}</h3>
                              <p className="text-sm text-gray-500 mt-1">{formatDate(video.created_at)}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {video.tier === 'premium' && (
                                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                                  <Crown className="h-3 w-3 mr-1" />
                                  Premium
                                </Badge>
                              )}
                              <Button
                                size="sm"
                                onClick={() => openShareModal(video)}
                                className="bg-purple-600 hover:bg-purple-500"
                                data-testid={`share-video-${video.id}`}
                              >
                                <Share2 className="h-4 w-4 mr-1" />
                                Share
                              </Button>
                            </div>
                          </div>
                          
                          {/* Stats Grid - Traffic Source Style */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                            <div className="bg-blue-600/20 rounded-lg px-3 py-2 border border-blue-500/30">
                              <div className="flex items-center gap-1.5">
                                <Eye className="h-4 w-4 text-blue-400" />
                                <span className="text-xs text-gray-400">Views</span>
                              </div>
                              <p className="text-lg font-bold text-white mt-1">{formatNumber(video.analytics?.views)}</p>
                            </div>
                            
                            <div className="bg-green-600/20 rounded-lg px-3 py-2 border border-green-500/30">
                              <div className="flex items-center gap-1.5">
                                <Clock className="h-4 w-4 text-green-400" />
                                <span className="text-xs text-gray-400">Watch Time</span>
                              </div>
                              <p className="text-lg font-bold text-white mt-1">{video.analytics?.watch_time_hours}h</p>
                            </div>
                            
                            <div className="bg-pink-600/20 rounded-lg px-3 py-2 border border-pink-500/30">
                              <div className="flex items-center gap-1.5">
                                <ThumbsUp className="h-4 w-4 text-pink-400" />
                                <span className="text-xs text-gray-400">Likes</span>
                              </div>
                              <p className="text-lg font-bold text-white mt-1">{video.analytics?.likes}</p>
                            </div>
                            
                            <div className="bg-purple-600/20 rounded-lg px-3 py-2 border border-purple-500/30">
                              <div className="flex items-center gap-1.5">
                                <MessageCircle className="h-4 w-4 text-purple-400" />
                                <span className="text-xs text-gray-400">Comments</span>
                              </div>
                              <p className="text-lg font-bold text-white mt-1">{video.analytics?.comments}</p>
                            </div>
                          </div>
                          
                          {/* Bottom Row */}
                          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-800">
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-gray-400">
                                Engagement: 
                                <span className={`ml-1 font-bold ${
                                  video.analytics?.engagement_rate > 5 ? 'text-green-400' : 
                                  video.analytics?.engagement_rate > 2 ? 'text-yellow-400' : 'text-gray-400'
                                }`}>
                                  {video.analytics?.engagement_rate}%
                                </span>
                              </span>
                              <span className="text-gray-400">
                                Unique Viewers: <span className="text-white font-medium">{video.analytics?.unique_viewers}</span>
                              </span>
                            </div>
                            <button
                              onClick={() => fetchVideoComments(video.id)}
                              className="flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300"
                            >
                              View Comments
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== REELS TAB ==================== */}
        {activeTab === 'reels' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Reels Analytics</h2>
                <p className="text-sm text-gray-400 mt-1">Performance metrics for your short-form content</p>
              </div>
              <Badge className="bg-pink-500/20 text-pink-400 border border-pink-500/30">
                {reels.length} reels
              </Badge>
            </div>
            
            {reels.length === 0 ? (
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-12 text-center">
                <Film className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400 text-lg">No reels assigned</p>
                <p className="text-sm text-gray-500 mt-1">Contact your admin to assign reels to your profile</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {reels.map((reel, idx) => (
                  <div key={reel.id} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden hover:border-pink-500/50 transition-colors">
                    <div className="aspect-[9/16] relative overflow-hidden bg-gray-800">
                      {reel.thumbnail_url ? (
                        <img 
                          src={reel.thumbnail_url} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="h-12 w-12 text-gray-600" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      <div className="absolute bottom-3 left-3 right-3">
                        <h3 className="font-medium text-white text-sm line-clamp-2">{reel.title}</h3>
                      </div>
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-2">
                      <div className="bg-blue-600/20 rounded-lg px-2 py-1.5 text-center border border-blue-500/30">
                        <Eye className="h-4 w-4 mx-auto text-blue-400" />
                        <p className="text-sm font-bold text-white mt-1">{formatNumber(reel.analytics?.views)}</p>
                        <p className="text-xs text-gray-400">Views</p>
                      </div>
                      <div className="bg-pink-600/20 rounded-lg px-2 py-1.5 text-center border border-pink-500/30">
                        <ThumbsUp className="h-4 w-4 mx-auto text-pink-400" />
                        <p className="text-sm font-bold text-white mt-1">{formatNumber(reel.analytics?.likes)}</p>
                        <p className="text-xs text-gray-400">Likes</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== SUBSCRIBERS TAB ==================== */}
        {activeTab === 'subscribers' && subscribers && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-6 border border-purple-500/30 text-center">
                <Users className="h-10 w-10 mx-auto text-purple-400 mb-3" />
                <p className="text-4xl font-bold text-white">{formatNumber(subscribers.total_subscribers)}</p>
                <p className="text-sm text-gray-400 mt-1">Total Subscribers</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/20 rounded-xl p-6 border border-green-500/30 text-center">
                <TrendingUp className="h-10 w-10 mx-auto text-green-400 mb-3" />
                <p className="text-4xl font-bold text-white">+{subscribers.recent_subscribers_30d}</p>
                <p className="text-sm text-gray-400 mt-1">Last 30 Days</p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-6 border border-blue-500/30 text-center">
                <BarChart3 className="h-10 w-10 mx-auto text-blue-400 mb-3" />
                <p className="text-4xl font-bold text-white">
                  {subscribers.growth?.length > 0 
                    ? Math.round(subscribers.growth.reduce((a, b) => a + b.new_subscribers, 0) / subscribers.growth.length)
                    : 0}
                </p>
                <p className="text-sm text-gray-400 mt-1">Avg Monthly Growth</p>
              </div>
            </div>
            
            {/* Growth Chart - Traffic Source Style */}
            {subscribers.growth?.length > 0 && (
              <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                <div className="p-4 border-b border-gray-800">
                  <h3 className="font-bold flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-purple-400" />
                    Subscriber Growth (Last 6 Months)
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {subscribers.growth.map((month, idx) => {
                      const maxVal = Math.max(...subscribers.growth.map(m => m.new_subscribers));
                      const pct = maxVal > 0 ? (month.new_subscribers / maxVal) * 100 : 0;
                      return (
                        <div key={month.month}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-300">{month.month}</span>
                            <span className="text-sm font-bold text-white">+{month.new_subscribers}</span>
                          </div>
                          <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all" 
                              style={{ 
                                width: `${Math.max(pct, 5)}%`, 
                                backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] 
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* ==================== AD REVENUE TAB ==================== */}
        {activeTab === 'ad-revenue' && creator?.show_ad_revenue && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-emerald-600/30 to-teal-600/20 rounded-xl p-5 border border-emerald-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="h-5 w-5 text-emerald-400" />
                  <span className="text-gray-300 text-sm">Total Ad Revenue</span>
                </div>
                <p className="text-4xl font-bold text-white">£{adRevenue?.total_ad_revenue?.toFixed(2) || '0.00'}</p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-5 border border-blue-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="h-5 w-5 text-blue-400" />
                  <span className="text-gray-300 text-sm">Total Impressions</span>
                </div>
                <p className="text-4xl font-bold text-white">{formatNumber(adRevenue?.total_impressions || 0)}</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-5 border border-purple-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="h-5 w-5 text-purple-400" />
                  <span className="text-gray-300 text-sm">CPM Rate</span>
                </div>
                <p className="text-4xl font-bold text-white">£{adRevenue?.cpm_rate?.toFixed(2) || '3.50'}</p>
                <p className="text-xs text-gray-400 mt-1">per 1,000 impressions</p>
              </div>
              
              <div className="bg-gradient-to-br from-yellow-600/30 to-orange-600/20 rounded-xl p-5 border border-yellow-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Video className="h-5 w-5 text-yellow-400" />
                  <span className="text-gray-300 text-sm">Videos with Ads</span>
                </div>
                <p className="text-4xl font-bold text-white">{adRevenue?.video_breakdown?.length || 0}</p>
              </div>
            </div>
            
            {/* Monthly Breakdown Chart */}
            {adRevenue?.monthly_breakdown && adRevenue.monthly_breakdown.length > 0 && (
              <div className="bg-gray-900/80 rounded-xl border border-gray-800 p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-emerald-400" />
                  Monthly Ad Revenue
                </h3>
                <div className="grid grid-cols-6 gap-2">
                  {adRevenue.monthly_breakdown.map((month, i) => (
                    <div key={i} className="text-center">
                      <div className="h-32 flex items-end justify-center mb-2">
                        <div 
                          className="w-8 bg-gradient-to-t from-emerald-600 to-teal-500 rounded-t-lg"
                          style={{ 
                            height: `${Math.max(8, (month.revenue / Math.max(...adRevenue.monthly_breakdown.map(m => m.revenue || 1))) * 100)}%`,
                            minHeight: '8px'
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-400">{month.month}</p>
                      <p className="text-sm font-bold text-emerald-400">£{month.revenue?.toFixed(2)}</p>
                      <p className="text-xs text-gray-500">{formatNumber(month.impressions)} imp</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Video Breakdown Table */}
            <div className="bg-gray-900/80 rounded-xl border border-gray-800 overflow-hidden">
              <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Video className="h-5 w-5 text-blue-400" />
                  Revenue by Video
                </h3>
              </div>
              
              {adRevenue?.video_breakdown?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-800/50">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Video</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Views</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Impressions</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {adRevenue.video_breakdown.map((video, i) => (
                        <tr key={i} className="hover:bg-gray-800/50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {video.thumbnail_url ? (
                                <img src={video.thumbnail_url} alt="" className="w-16 h-9 object-cover rounded bg-gray-800" />
                              ) : (
                                <div className="w-16 h-9 bg-gray-800 rounded flex items-center justify-center">
                                  <Video className="h-4 w-4 text-gray-600" />
                                </div>
                              )}
                              <span className="text-sm font-medium text-white truncate max-w-xs">{video.title}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-gray-300">{formatNumber(video.view_count)}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-300">{formatNumber(video.impressions)}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-bold text-emerald-400">£{video.revenue?.toFixed(2)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No ad revenue data yet</p>
                  <p className="text-sm mt-1">Ad revenue will appear here once your videos start showing ads</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* ==================== MODALS ==================== */}
      
      {/* Comments Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-2xl max-h-[80vh] overflow-hidden bg-gray-900 rounded-xl border border-gray-800">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white">Comments</h3>
                <p className="text-sm text-gray-400 truncate max-w-md">{selectedVideo.title}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setSelectedVideo(null); setVideoComments([]); }}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {videoComments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No comments yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {videoComments.map((comment, i) => (
                    <div key={i} className="p-4 bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-sm font-bold">
                          {comment.customer_name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{comment.customer_name || 'Anonymous'}</p>
                          <p className="text-xs text-gray-500">{formatDate(comment.created_at)}</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-300">{comment.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Account Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-md bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Account Settings
              </h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowSettings(false); setSettingsError(''); setSettingsSuccess(''); }}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-6 space-y-6">
              {settingsError && (
                <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {settingsError}
                </div>
              )}
              
              {settingsSuccess && (
                <div className="p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 text-sm flex items-center gap-2">
                  <Check className="h-4 w-4 flex-shrink-0" />
                  {settingsSuccess}
                </div>
              )}

              {/* Change Email */}
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2 text-white">
                  <Mail className="h-4 w-4 text-blue-400" />
                  Change Email
                </h4>
                <div className="space-y-2">
                  <p className="text-sm text-gray-400">Current: {creator?.email}</p>
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="New email address"
                    className="bg-gray-800 border-gray-700"
                  />
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Confirm with password"
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
                <Button onClick={handleUpdateEmail} disabled={savingSettings} className="w-full bg-blue-600 hover:bg-blue-500">
                  {savingSettings ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Update Email
                </Button>
              </div>

              <div className="border-t border-gray-800" />

              {/* Change Password */}
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2 text-white">
                  <Lock className="h-4 w-4 text-purple-400" />
                  Change Password
                </h4>
                <div className="space-y-2">
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current password"
                    className="bg-gray-800 border-gray-700"
                  />
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password (min 8 characters)"
                    className="bg-gray-800 border-gray-700"
                  />
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
                <Button onClick={handleChangePassword} disabled={savingSettings} className="w-full bg-purple-600 hover:bg-purple-500">
                  {savingSettings ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Change Password
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Video Modal */}
      {shareVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-md bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Share2 className="h-5 w-5 text-purple-400" />
                Share Video
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShareVideo(null)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              {/* Video Preview */}
              <div className="flex gap-3 items-start">
                <div className="w-24 aspect-video rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                  {shareVideo.thumbnail_url ? (
                    <img src={shareVideo.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Play className="h-6 w-6 text-gray-600" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-white line-clamp-2">{shareVideo.title}</h4>
                  <p className="text-sm text-gray-400 mt-1">{formatNumber(shareVideo.analytics?.views)} views</p>
                </div>
              </div>

              {/* Share URL */}
              <div className="space-y-2">
                <Label className="text-gray-400 text-sm">Share Link</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={getVideoShareUrl(shareVideo)}
                    className="bg-gray-800 border-gray-700 text-white font-mono text-sm"
                  />
                  <Button
                    onClick={() => copyShareUrl(shareVideo)}
                    className={shareCopied ? 'bg-green-600 hover:bg-green-500' : 'bg-purple-600 hover:bg-purple-500'}
                  >
                    {shareCopied ? <><Check className="h-4 w-4 mr-1" /> Copied!</> : <><Copy className="h-4 w-4 mr-1" /> Copy</>}
                  </Button>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={() => window.open(getVideoShareUrl(shareVideo), '_blank')}
                className="w-full border-gray-700"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in New Tab
              </Button>

              {/* Social Share */}
              <div className="pt-3 border-t border-gray-800">
                <p className="text-xs text-gray-500 mb-3">Share on social media</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(getVideoShareUrl(shareVideo))}&text=${encodeURIComponent(shareVideo.title)}`, '_blank')}
                    className="flex-1 border-gray-700"
                  >
                    𝕏 Twitter
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getVideoShareUrl(shareVideo))}`, '_blank')}
                    className="flex-1 border-gray-700"
                  >
                    Facebook
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareVideo.title + ' ' + getVideoShareUrl(shareVideo))}`, '_blank')}
                    className="flex-1 border-gray-700"
                  >
                    WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorPortalDashboard;
